import { GroupByNodeDataSchema } from '@cotera/api';
import type { WorkflowNodeData } from '../types';
import type { WorkflowNodeHydration } from '../workflow-node-base';
import type { WorkflowNodeOutputShapeContext } from '../workflow-node-output-shape';
import { displayGroupByOutputShape } from './output-shape-helpers';
import { TypedWorkflowNode } from './typed-workflow-node';

/**
 * A group-by node splits one list on a field and emits one entry per distinct
 * value, so a `for-each` below it fans out one branch per group.
 *
 * Like a bucket, it does not pass its input through: its output is
 * `{ groups, groupCount, itemCount }`, not its predecessor's shape. It derives
 * exactly that envelope here so the field pickers below it can offer `/groups`,
 * `/groups/0/key`, `/groups/0/count`, `/groupCount` and `/itemCount` rather than
 * one opaque "whole group-by output".
 *
 * The grouped item's own fields are left generic (see
 * {@link displayGroupByOutputShape}): resolving them would need the predecessor's
 * runtime sample, which the editor has but the save-time validator does not, so a
 * `/groups/0/items/0/<field>` ref would be offered and then rejected on save.
 * Per-item field work belongs in a for-each over `/groups`.
 */
export class GroupByWorkflowNode extends TypedWorkflowNode<
  typeof GroupByNodeDataSchema
> {
  protected readonly schema = GroupByNodeDataSchema;

  static override create(
    data: WorkflowNodeData,
    hydration: WorkflowNodeHydration
  ): GroupByWorkflowNode {
    return new GroupByWorkflowNode(data, hydration);
  }

  override deriveTypedOutputShape(
    _context: WorkflowNodeOutputShapeContext
  ): string | null {
    return displayGroupByOutputShape();
  }
}
